import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { journeyMemoryApi } from '../../api/journeyMemoryApi';
import { journeyStoryApi } from '../../api/journeyStoryApi';
import { normalizeMemoryPage } from '../journey-journal/journeyMemoryModel';
import { useTravelText } from '../../app/TravelLocaleContext';
import {
  consentLabelKey,
  memoryTypeLabelKey,
  mergeStoryCandidates,
  moveSelected,
  normalizeOwnerStory,
  normalizeStoryRevision,
  publishableLabelKey,
  STORY_TIMING_NOTICE_KEY,
  storyErrorKind,
  storyErrorMessageKey,
} from './journeyStoryModel';

const C = 'travel.publication.story.curation';

/**
 * 行程擁有者整理公開故事：選入／取消選入／上下移動排序。
 * 寫入成功後一律以伺服器資料重新載入，不把待發布內容當成已公開。
 */
export default function JourneyStoryCurationPanel({
  journeyId,
  ownerMemberId = null,
  reloadToken = 0,
  disabled = false,
  onRevisionChange,
  onStoryChanged,
}) {
  const generation = useRef(0);
  const disposed = useRef(false);
  const busyRef = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [story, setStory] = useState(null);
  const [rows, setRows] = useState({ selected: [], unselected: [] });
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const headingId = useId();
  const { t, tf } = useTravelText();

  const reportRevision = useCallback((revision) => {
    onRevisionChange?.(revision);
  }, [onRevisionChange]);

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) setPhase('loading');
    try {
      const [storyPayload, memoryPayload] = await Promise.all([
        journeyStoryApi.listItems(journeyId),
        journeyMemoryApi.list(journeyId, { limit: 50 }).catch(() => null),
      ]);
      if (requestId !== generation.current || disposed.current) return;
      const normalized = normalizeOwnerStory(storyPayload);
      const memories = memoryPayload ? normalizeMemoryPage(memoryPayload).items : [];
      setStory(normalized);
      setRows(mergeStoryCandidates(normalized, memories, { ownerMemberId }));
      reportRevision(normalized.storyRevision);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current || disposed.current) return;
      const kind = storyErrorKind(cause);
      setError(storyErrorMessageKey(kind));
      reportRevision(null);
      setPhase(kind === 'forbidden' || kind === 'not-found' ? kind : 'error');
    }
  }, [journeyId, ownerMemberId, reportRevision]);

  useEffect(() => {
    disposed.current = false;
    setError('');
    setFeedback('');
    load();
    return () => {
      disposed.current = true;
      generation.current += 1;
    };
  }, [load, reloadToken]);

  async function runWrite(memoryEntryId, action, successMessage) {
    if (busyRef.current || disabled) return;
    busyRef.current = true;
    setPendingId(memoryEntryId);
    setError('');
    setFeedback('');
    try {
      const response = normalizeStoryRevision(await action());
      if (disposed.current) return;
      if (response.storyRevision !== null) reportRevision(response.storyRevision);
      await load({ soft: true });
      if (disposed.current) return;
      setFeedback(successMessage);
      onStoryChanged?.();
    } catch (cause) {
      if (disposed.current) return;
      const kind = storyErrorKind(cause);
      setError(storyErrorMessageKey(kind));
      if (kind !== 'network' && kind !== 'unauthorized') await load({ soft: true });
    } finally {
      busyRef.current = false;
      if (!disposed.current) setPendingId(null);
    }
  }

  function toggle(row) {
    const selecting = !row.ownerSelected;
    runWrite(
      row.memoryEntryId,
      () => journeyStoryApi.setSelection(journeyId, row.memoryEntryId, selecting),
      selecting ? `${C}.selectedFeedback` : `${C}.deselectedFeedback`,
    );
  }

  function move(index, delta) {
    const ids = moveSelected(rows.selected.map((row) => row.memoryEntryId), index, delta);
    if (!ids || story?.storyRevision === null || story?.storyRevision === undefined) return;
    runWrite(
      rows.selected[index].memoryEntryId,
      () => journeyStoryApi.reorder(journeyId, { memoryEntryIds: ids, expectedStoryRevision: story.storyRevision }),
      `${C}.reorderedFeedback`,
    );
  }

  const busy = Boolean(pendingId) || disabled;

  function renderRow(row, index, selectedList) {
    const title = row.title || t('travel.publication.story.untitled');
    return (
      <li key={row.memoryEntryId} className="journey-story__row" data-memory-entry-id={row.memoryEntryId}>
        <div className="journey-story__meta">
          <strong className="journey-story__title">{title}</strong>
          {row.summary ? <p className="journey-story__summary">{row.summary}</p> : null}
          <p className="journey-story__tags">
            <span>{t(memoryTypeLabelKey(row.memoryType))}</span>
            <span>{t(row.authoredByOwner ? `${C}.authorOwner` : `${C}.authorCompanion`)}</span>
            <span>{t(row.ownerSelected ? `${C}.selected` : `${C}.unselected`)}</span>
            <span>{t(consentLabelKey(row))}</span>
            <span>{t(publishableLabelKey(row))}</span>
          </p>
        </div>
        <div className="journey-story__row-actions">
          {selectedList ? (
            <>
              <button
                type="button"
                aria-label={tf(`${C}.moveUpLabel`, { title })}
                disabled={busy || index === 0}
                onClick={() => move(index, -1)}
              >
                {t(`${C}.moveUp`)}
              </button>
              <button
                type="button"
                aria-label={tf(`${C}.moveDownLabel`, { title })}
                disabled={busy || index === rows.selected.length - 1}
                onClick={() => move(index, 1)}
              >
                {t(`${C}.moveDown`)}
              </button>
            </>
          ) : null}
          <button
            type="button"
            aria-pressed={row.ownerSelected}
            aria-label={tf(row.ownerSelected ? `${C}.deselectLabel` : `${C}.selectLabel`, { title })}
            disabled={busy}
            onClick={() => toggle(row)}
          >
            {pendingId === row.memoryEntryId
              ? t('travel.publication.story.updating')
              : t(row.ownerSelected ? `${C}.deselect` : `${C}.select`)}
          </button>
        </div>
      </li>
    );
  }

  return (
    <section className="journey-story" aria-labelledby={headingId} data-testid="journey-story-curation">
      <h4 id={headingId}>{t(`${C}.heading`)}</h4>
      <p className="journey-publication__hint" data-testid="journey-story-timing">{t(STORY_TIMING_NOTICE_KEY)}</p>
      <p className="journey-publication__hint">{t(`${C}.companionHint`)}</p>
      {phase === 'loading' ? <p className="journey-publication__pending" aria-live="polite">{t(`${C}.loading`)}</p> : null}
      {error ? <p className="journey-publication__error" role="alert">{t(error)}</p> : null}
      {feedback ? <p className="journey-publication__status" role="status">{t(feedback)}</p> : null}
      {phase === 'error' ? (
        <div className="journey-publication__actions">
          <button type="button" onClick={() => { setError(''); load(); }}>{t(`${C}.reload`)}</button>
        </div>
      ) : null}
      {phase === 'ready' && !rows.selected.length && !rows.unselected.length ? (
        <p className="journey-publication__hint" data-testid="journey-story-empty">
          {t(`${C}.empty`)}
        </p>
      ) : null}
      {phase === 'ready' && rows.selected.length ? (
        <>
          <h5 className="journey-story__group">{t(`${C}.selectedHeading`)}</h5>
          <ol className="journey-story__list" data-testid="journey-story-selected">
            {rows.selected.map((row, index) => renderRow(row, index, true))}
          </ol>
        </>
      ) : null}
      {phase === 'ready' && rows.unselected.length ? (
        <>
          <h5 className="journey-story__group">{t(`${C}.unselectedHeading`)}</h5>
          <ul className="journey-story__list" data-testid="journey-story-unselected">
            {rows.unselected.map((row, index) => renderRow(row, index, false))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
